/**
 * weblabsShare — encode/decode the Weblab Visual Dashboard's filter state to/
 * from URL query params, so a "Share view" link opens the dashboard with the
 * exact same filters (date range, marketplace, SP/SB, activation state, weblab
 * type, start/launch-date range) and grouping. Mirrors the Revenue Bridging
 * dashboard's shareUtils, but for this plugin's own (self-managed) filters.
 */

import type { WeblabMetric, Grouping } from "./weblabsChart";

/** The shareable Weblab view: the applied filters + the current grouping. */
export interface WeblabsShareState {
  startDate?: string;
  endDate?: string;
  country?: string;
  metric?: WeblabMetric;
  activationState?: string;
  weblabType?: string;
  runStart?: string;
  runEnd?: string;
  grouping?: Grouping;
}

/** Short, stable query-param keys. */
const PARAM = {
  start: "wvs",
  end: "wve",
  country: "wvmkt",
  metric: "wvm",
  activationState: "wvact",
  weblabType: "wvtype",
  runStart: "wvrs",
  runEnd: "wvre",
  grouping: "wvg",
} as const;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const GROUPINGS: Grouping[] = ["D", "W", "M", "Q"];

/** Build an absolute shareable URL for the Weblab Visual Dashboard view. */
export function buildWeblabsShareUrl(state: WeblabsShareState): string {
  const params = new URLSearchParams(window.location.search);
  // Land the recipient on this dashboard.
  params.set("view", "dashboard");
  params.set("dashboard", "weblabs-visual");
  if (state.startDate) params.set(PARAM.start, state.startDate);
  if (state.endDate) params.set(PARAM.end, state.endDate);
  if (state.country) params.set(PARAM.country, state.country);
  if (state.metric)
    params.set(
      PARAM.metric,
      state.metric === "impact_sp_rev_day" ? "sp" : "sb"
    );
  if (state.activationState)
    params.set(PARAM.activationState, state.activationState);
  if (state.weblabType) params.set(PARAM.weblabType, state.weblabType);
  if (state.runStart) params.set(PARAM.runStart, state.runStart);
  if (state.runEnd) params.set(PARAM.runEnd, state.runEnd);
  if (state.grouping) params.set(PARAM.grouping, state.grouping);
  return `${window.location.origin}${
    window.location.pathname
  }?${params.toString()}`;
}

/**
 * Read Weblab filter params from the current URL. Returns only the values
 * present and valid, or null if none of the Weblab params are present (so the
 * caller can distinguish "no shared filters" from "shared with defaults").
 */
export function parseWeblabsShareUrl(): WeblabsShareState | null {
  const params = new URLSearchParams(window.location.search);
  const has = Object.values(PARAM).some((k) => params.has(k));
  if (!has) return null;

  const out: WeblabsShareState = {};
  const start = params.get(PARAM.start);
  if (start && DATE_RE.test(start)) out.startDate = start;
  const end = params.get(PARAM.end);
  if (end && DATE_RE.test(end)) out.endDate = end;

  const country = params.get(PARAM.country);
  if (country) out.country = country;

  const metric = params.get(PARAM.metric);
  if (metric === "sp") out.metric = "impact_sp_rev_day";
  else if (metric === "sb") out.metric = "impact_sb_rev_day";

  const act = params.get(PARAM.activationState);
  if (act) out.activationState = act;
  const wt = params.get(PARAM.weblabType);
  if (wt) out.weblabType = wt;

  const rs = params.get(PARAM.runStart);
  if (rs && DATE_RE.test(rs)) out.runStart = rs;
  const re = params.get(PARAM.runEnd);
  if (re && DATE_RE.test(re)) out.runEnd = re;

  const g = params.get(PARAM.grouping);
  if (g && GROUPINGS.includes(g as Grouping)) out.grouping = g as Grouping;

  return out;
}
